import type React from 'react';
import { originalFromRender } from '../../lib/imageUrl';

/**
 * Visual-only course-thumbnail fallback. When a course has no `thumbnailUrl`
 * (or the URL 404s), an `<img>` would otherwise render a torn-image glyph on
 * the marketing pages and catalogue cards. Use both together:
 *
 *   <img src={thumbnailSrc(course.thumbnailUrl) || THUMB_PLACEHOLDER} onError={onThumbError} />
 *
 * `src || THUMB_PLACEHOLDER` covers the empty/missing case (an empty src never
 * fires `error` in some browsers); `onError` covers a broken/expired URL.
 * Neutral stone gradient + faint open-book mark so it reads as intentional on
 * any of the three marketing themes.
 *
 * Two-step fallback: a `thumbnailSrc` render URL (lib/imageUrl.ts) that fails
 * first retries the untransformed original — so if Supabase image transforms
 * are ever disabled on the project, cards degrade to the full-size image, not
 * to the placeholder. Only a failure of the original itself lands there.
 */
export const THUMB_PLACEHOLDER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%23f5f5f4'/%3E%3Cstop offset='1' stop-color='%23e7e5e4'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='400' height='300' fill='url(%23g)'/%3E%3Cg fill='none' stroke='%23c4c0bb' stroke-width='6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M200 116c-14-9-34-12-52-12-7 0-13 5-13 12v64c0 4 4 7 9 6 16-2 38 1 50 9 13-8 34-11 50-9 5 1 9-2 9-6v-64c0-7-6-12-13-12-18 0-38 3-52 12z'/%3E%3Cpath d='M200 116v82'/%3E%3C/g%3E%3C/svg%3E";

export const onThumbError: React.ReactEventHandler<HTMLImageElement> = (e) => {
  const img = e.currentTarget;
  if (img.dataset.fb === '1') return; // already swapped — avoid an error loop
  if (img.dataset.fb !== 'orig') {
    const original = originalFromRender(img.src);
    if (original) {
      img.dataset.fb = 'orig';
      img.src = original;
      return;
    }
  }
  img.dataset.fb = '1';
  img.src = THUMB_PLACEHOLDER;
};
